import relationGateway from '@/core/api/gateways/relationGateway';
import {
    HISTORY_LEGACY_FALLBACK_CLEANUP_MARKER,
    mapHistoryResponseToCanonicalGroups
} from '@/core/api/mappers/historyRelationMapper';

const normalizeLegacyScopeString = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

export const buildLegacyHashPresetScope = ({
    originalHashId,
    presetStyleId = null
} = {}) => {
    const normalizedOriginalHashId = normalizeLegacyScopeString(originalHashId);
    const normalizedPresetStyleId = normalizeLegacyScopeString(presetStyleId);
    return {
        originalHashId: normalizedOriginalHashId,
        presetStyleId: normalizedPresetStyleId,
        scopeType: normalizedPresetStyleId ? 'reference' : 'solo',
        scopeKey: normalizedOriginalHashId
            ? `${normalizedOriginalHashId}::${normalizedPresetStyleId || '__solo__'}`
            : ''
    };
};

const relationLegacyAdapter = {
    saveRelation(payload) {
        return relationGateway.saveRelation(payload);
    },

    fetchRelation(hashId, options = {}) {
        return relationGateway.fetchRelation(hashId, options);
    },

    async listHistory({ params = {}, headers = {} } = {}) {
        const rawResponse = await relationGateway.listHistory({ params, headers });
        const responseData = rawResponse.data ?? rawResponse;
        const rawDetail = Array.isArray(responseData?.detail) ? responseData.detail : [];
        const rawHistoryGroups = Array.isArray(responseData?.history_groups) ? responseData.history_groups : [];
        const sortedImages = [...rawDetail].sort((a, b) =>
            new Date(b.updated_at) - new Date(a.updated_at)
        );

        try {
            return {
                success: true,
                data: mapHistoryResponseToCanonicalGroups({
                    detailList: sortedImages,
                    historyGroups: rawHistoryGroups
                }),
                dataSource: 'real',
                scenarioId: null,
                meta: {
                    rawDetail: sortedImages,
                    rawHistoryGroups,
                    requiresLegacyFallback: false,
                    cleanupMarker: HISTORY_LEGACY_FALLBACK_CLEANUP_MARKER
                }
            };
        } catch (mapperError) {
            console.warn('[relationLegacyAdapter] history canonical mapper failed, delegating fallback to store:', mapperError);

            return {
                success: true,
                data: [],
                error: 'history_mapper_failed',
                dataSource: 'real',
                scenarioId: null,
                meta: {
                    rawDetail: sortedImages,
                    rawHistoryGroups,
                    requiresLegacyFallback: true,
                    mapperError: mapperError?.message || 'history canonical mapper failed',
                    cleanupMarker: HISTORY_LEGACY_FALLBACK_CLEANUP_MARKER
                }
            };
        }
    },

    deleteHistoryGroup(params) {
        return relationGateway.deleteHistoryGroup(params);
    },

    touchRelation(hashId, options = {}) {
        return relationGateway.touchRelation(hashId, options);
    },

    restoreRelation(hashId, relationId) {
        return relationGateway.restoreRelation(hashId, relationId);
    },

    createOrUpdateRelation(payload) {
        return relationGateway.createOrUpdateRelation(payload);
    },

    deleteImage(hashId, userImageRelationId) {
        return relationGateway.deleteImage(hashId, userImageRelationId);
    },

    async getUserImages({ params = {}, headers = {} } = {}) {
        return relationGateway.getUserImages({ params, headers });
    }
};

export default relationLegacyAdapter;
